import React, { ReactNode } from "react"
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useSearchParams,
} from "react-router-dom"
import LoginPage from "./pages/LoginPage"
import ChangePasswordPage from "./pages/ChangePasswordPage"
import ForgotPasswordPage from "./pages/ForgotPasswordPage"
import ResetPasswordPage from "./pages/ResetPasswordPage"
import HomePage from "./pages/HomePage"
import UserManagementPage from "./pages/admin/UserManagementPage"
import InsightsDashboardPage from "./pages/hq/InsightsDashboardPage"
import ScheduledReportsPage from "./pages/hq/ScheduledReportsPage"
import AnnouncementsPage from "./pages/hq/AnnouncementsPage"
import PhotoAIPage from "./pages/hq/PhotoAIPage"
import UsageTracker from "./components/UsageTracker"
import IntegrationsPage from "./pages/admin/IntegrationsPage"
import MyAccountPage from "./pages/settings/MyAccountPage"
import InventoryAuditPage from "./pages/inventory/InventoryAuditPage"
import PerformanceReportPage from "./pages/inventory/PerformanceReportPage"
import NotOnWebsitePage from "./pages/inventory/NotOnWebsitePage"
import DealerScheduledReportsPage from "./pages/reports/DealerScheduledReportsPage"
import NewAuditRunPage from "./pages/inventory/NewAuditRunPage"
import VehicleInventoryPage from "./pages/inventory/VehicleInventoryPage"
import VehicleHistoryPage from "./pages/inventory/VehicleHistoryPage"
import UpdateInventoryPage from "./pages/inventory/UpdateInventoryPage"
import AdesaClearedListPage from "./pages/inventory/wholesale/AdesaClearedListPage"
import AdesaRunListPage from "./pages/inventory/wholesale/AdesaRunListPage"
import WholesaleReportsPage from "./pages/inventory/wholesale/WholesaleReportsPage"
import WholesalePreferencesPage from "./pages/inventory/wholesale/WholesalePreferencesPage"
import DailyPhotoListPage from "./pages/photos/DailyPhotoListPage"
import PhotoListNewRunPage from "./pages/photos/PhotoListNewRunPage"
import PhotoListSettingsPage from "./pages/photos/PhotoListSettingsPage"
import PhotosOverviewPage from "./pages/photos/PhotosOverviewPage"
import ImageIndexPage from "./pages/photos/ImageIndexPage"
import PhotoConfigurationPage from "./pages/photos/PhotoConfigurationPage"
import VehicleDetailPage from "./pages/photos/VehicleDetailPage"
import Layout from "./components/Layout"
import { useAuth } from "./context/AuthContext"
import { DealerGroupProvider } from "./context/DealerGroupContext"
import { RunsProvider } from "./context/RunsContext"
import { THEME } from "./config/theme"

const TOKEN_KEY = "invi_token"

const hasToken = (): boolean => localStorage.getItem(TOKEN_KEY) !== null

// Wraps protected routes. Always requires a token. When `requiresPermission`
// is set, the current user must also hold that permission — otherwise we
// redirect to /dashboard rather than rendering the page. Closes the gap
// where sidebar entries are permission-gated but their routes weren't.
//
// Permission checks must wait for AuthContext to finish loading the user,
// or a hand-typed URL like /photos/vehicles/123 races against /auth/me
// and bounces to /dashboard before the user object exists.
const ProtectedRoute: React.FC<{
  children: ReactNode
  requiresPermission?: string
}> = ({ children, requiresPermission }) => {
  const { hasPermission, isLoading } = useAuth()
  if (!hasToken()) {
    return <Navigate to="/login" replace />
  }
  if (isLoading) {
    return null
  }
  if (requiresPermission && !hasPermission(requiresPermission)) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

// The standalone Website Audit page is retired — its sheets live as tabs on
// the Audit page. Old links (bookmarks, home cards) carry their sheet along.
const WebsiteAuditRedirect: React.FC = () => {
  const [params] = useSearchParams()
  const next = new URLSearchParams()
  const sheet = params.get("sheet") ?? params.get("wsheet")
  if (sheet) next.set("wsheet", sheet)
  const group = params.get("group")
  if (group) next.set("group", group)
  const query = next.toString()
  return <Navigate to={`/inventory/audit${query ? `?${query}` : ""}`} replace />
}

// Wraps /login — redirects to /dashboard if already logged in
const LoginGate: React.FC<{ children: ReactNode }> = ({ children }) => {
  if (hasToken()) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

// Placeholder page for not-yet-built routes
const Placeholder: React.FC<{ name: string }> = ({ name }) => (
  <Layout>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        fontSize: "20px",
        color: THEME.colors.neutral.light.textSecondary,
        fontFamily: THEME.fonts.primary,
      }}
    >
      {name}
    </div>
  </Layout>
)

const PLACEHOLDER_ROUTES: { path: string; name: string; requiresPermission?: string }[] = [
  { path: "/search",               name: "Search" },
  // INVI HQ → Data roadmap (system-admin only). Built items have real routes;
  // these are placeholders that double as the build to-do list.
  { path: "/hq/data/quality",      name: "Data Quality",      requiresPermission: "view_insights" },
  { path: "/hq/data/schema",       name: "Schema & Mappings", requiresPermission: "view_insights" },
  { path: "/hq/data/retention",    name: "Retention",         requiresPermission: "view_insights" },
  { path: "/hq/data/backups",      name: "Backups",           requiresPermission: "view_insights" },
]

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <DealerGroupProvider>
      <RunsProvider>
      <UsageTracker />
      <Routes>
        <Route
          path="/"
          element={
            hasToken() ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/login"
          element={
            <LoginGate>
              <LoginPage />
            </LoginGate>
          }
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <UserManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hq/insights"
          element={
            <ProtectedRoute requiresPermission="view_insights">
              <InsightsDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hq/scheduled-reports"
          element={
            <ProtectedRoute requiresPermission="view_insights">
              <ScheduledReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hq/announcements"
          element={
            <ProtectedRoute requiresPermission="view_insights">
              <AnnouncementsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hq/photo-ai"
          element={
            <ProtectedRoute requiresPermission="view_insights">
              <PhotoAIPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/integrations"
          element={
            <ProtectedRoute>
              <IntegrationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/audit"
          element={
            <ProtectedRoute>
              <InventoryAuditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/audit/new-run"
          element={
            <ProtectedRoute requiresPermission="run_audit">
              <NewAuditRunPage />
            </ProtectedRoute>
          }
        />
        {/* Nested under the audit rather than a sibling of it: this reports on
            how the audit tool is doing, and "Performance" sitting in the
            Inventory nav read as inventory performance — turn, aging, sales
            (Ian, 2026-08-13). Reached from a button on the audit page. */}
        <Route
          path="/inventory/audit/performance"
          element={
            <ProtectedRoute requiresPermission="view_inventory_audit">
              <PerformanceReportPage />
            </ProtectedRoute>
          }
        />
        {/* The cars that should be on the website and are not, and what they
            are worth (revenue-assurance plan, Phase 3). Nested under the audit
            like Tool Performance; reached from the tile at the end of the
            audit page's band. */}
        <Route
          path="/inventory/audit/not-on-website"
          element={
            <ProtectedRoute requiresPermission="view_inventory_audit">
              <NotOnWebsitePage />
            </ProtectedRoute>
          }
        />
        {/* The dealer's Scheduled Reports — the sidebar entry that was a
            placeholder from the first commit. Its first section is the morning
            audit alerts (revenue-assurance plan, Phase 2); the audit page's
            Alerts button deep-links here. The permission is the one the
            backend router requires. */}
        <Route
          path="/reports/scheduled"
          element={
            <ProtectedRoute requiresPermission="manage_audit_alerts">
              <DealerScheduledReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/vehicles"
          element={
            <ProtectedRoute requiresPermission="view_inventory_audit">
              <VehicleInventoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/vehicles/:vin/:stock"
          element={
            <ProtectedRoute requiresPermission="view_inventory_audit">
              <VehicleHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/update"
          element={
            <ProtectedRoute requiresPermission="run_audit">
              <UpdateInventoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/website"
          element={<WebsiteAuditRedirect />}
        />
        <Route
          path="/inventory/wholesale/adesa-cleared-list"
          element={
            <ProtectedRoute requiresPermission="view_wholesale">
              <AdesaClearedListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/wholesale/adesa-run-list"
          element={
            <ProtectedRoute requiresPermission="view_wholesale">
              <AdesaRunListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/wholesale/reports"
          element={
            <ProtectedRoute requiresPermission="view_wholesale">
              <WholesaleReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory/wholesale/preferences"
          element={
            <ProtectedRoute requiresPermission="view_wholesale">
              <WholesalePreferencesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/photos/daily"
          element={
            <ProtectedRoute>
              <DailyPhotoListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/photos/daily/new-run"
          element={
            <ProtectedRoute>
              <PhotoListNewRunPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/photos/daily/settings"
          element={
            <ProtectedRoute requiresPermission="manage_photo_list_settings">
              <PhotoListSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/photos/overview"
          element={
            <ProtectedRoute requiresPermission="view_image_index">
              <PhotosOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/photos/image-index"
          element={
            <ProtectedRoute requiresPermission="view_image_index">
              <ImageIndexPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/photos/vehicles/:vin"
          element={
            <ProtectedRoute requiresPermission="view_image_index">
              <VehicleDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          // Parallel route for stock-only inventory (CTA campers,
          // trailers, accessory bundles with no VIN). Same component;
          // the page distinguishes via useParams() and loads from
          // /image-index/items/{stock} instead of /vehicles/{vin}.
          path="/photos/items/:stock"
          element={
            <ProtectedRoute requiresPermission="view_image_index">
              <VehicleDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/photos/quality-control"
          element={
            <ProtectedRoute requiresPermission="view_image_index">
              <Placeholder name="Quality Control" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/photos/configuration"
          element={
            <ProtectedRoute requiresPermission="manage_photo_configuration">
              <PhotoConfigurationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/account"
          element={
            <ProtectedRoute>
              <MyAccountPage />
            </ProtectedRoute>
          }
        />
        {PLACEHOLDER_ROUTES.map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={
              <ProtectedRoute requiresPermission={r.requiresPermission}>
                <Placeholder name={r.name} />
              </ProtectedRoute>
            }
          />
        ))}
        {/* Catch-all route. An unknown URL (e.g. a stale /admin/data bookmark
            from before Data Management was retired) bounces to "/", which then
            routes to the dashboard or login by auth — instead of a blank page. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </RunsProvider>
      </DealerGroupProvider>
    </BrowserRouter>
  )
}

export default App
